<script setup lang="ts">
import { computed } from 'vue'
import { Dialog } from '@ark-ui/vue'
import UiText from './UiText.vue'
import UiButton from './UiButton/index'
import type { ButtonVariant } from './UiButton/types'
import UiIcon from './UiIcon.vue'

export interface UiDialogProps {
    title?: string
    text?: string
    primaryActionLabel?: string
    secondaryActionLabel?: string
    hideCloseButton?: boolean
    arrowsOnActionButtons?: boolean
    primaryActionVariant?: ButtonVariant
    /** Hide the body scrollbar without disabling scrolling. */
    hideScrollbar?: boolean
    // Width presets for modal panel
    size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl'
    /** Optional narrower panel width; the panel still fits the viewport. */
    maxWidth?: string
    /** Compact panel with a corner close button. */
    appearance?: 'default' | 'compact'
}

const props = withDefaults(defineProps<UiDialogProps>(), {
    title: '',
    text: '',
    primaryActionLabel: undefined,
    secondaryActionLabel: undefined,
    hideCloseButton: false,
    arrowsOnActionButtons: false,
    primaryActionVariant: 'primary',
    hideScrollbar: false,
    size: 'md',
    appearance: 'default',
})

const emit = defineEmits<{
    (e: 'open'): void
    (e: 'close'): void
    (e: 'primaryAction'): void
    (e: 'secondaryAction'): void
}>()

// v-model to control open state from parent
const model = defineModel<boolean>({ default: false })
const isLoading = defineModel<boolean>('loading', { default: false })

function close() {
    model.value = false
    emit('close')
}

function open() {
    model.value = true
    emit('open')
}

function onOpenChange(details: any) {
    const next = typeof details === 'object' && details ? !!details.open : !!details
    if (next) emit('open')
    else emit('close')
}

const sizeClass = computed(() => (
    // Todo(Khaled): Create a utility class for this
    props.size === 'sm'
        ? 'max-w-md'
        : props.size === '2xl'
            ? 'max-w-[min(calc(100dvw-var(--spacing)*8),1440px)]'
            : props.size === 'xl'
                ? 'max-w-[min(calc(100dvw-var(--spacing)*8),860px)]'
                : props.size === 'lg'
                    ? 'max-w-[min(calc(100dvw-var(--spacing)*8),550px)]'
                    : 'max-w-[min(calc(100dvw-var(--spacing)*8),450px)] mx-4'
))

defineExpose({ open, close })
</script>

<template>
    <Teleport to="body" defer>
        <Dialog.Root v-model:open="model" @openChange="onOpenChange" :close-on-escape="!props.hideCloseButton" :close-on-click-outside="!props.hideCloseButton" lazyMount>
            <Dialog.Backdrop class="se-dialog-overlay fixed inset-0 z-100" :class="props.appearance === 'compact' ? 'bg-se-dark/35' : 'bg-black/40'" />
            <Dialog.Positioner class="fixed inset-0 z-100 flex items-center justify-center p-4">
                <Dialog.Content class="relative pointer-events-auto bg-white w-full flex flex-col gap-6 se-max-dialog-height" :class="[sizeClass, props.appearance === 'compact' ? 'rounded-lg p-6' : 'rounded-2xl p-8']" :style="props.maxWidth ? { maxWidth: props.maxWidth } : undefined">
                    <!-- Header -->
                    <div class="flex flex-col gap-2">
                        <div class="flex-1 min-w-0 flex items-center justify-between">
                            <Dialog.Title>
                                <UiText v-if="props.title" as="h4" :text="props.title"
                                    :class="props.appearance === 'compact' ? 'font-medium' : 'font-semibold'" />
                            </Dialog.Title>
                            <Dialog.CloseTrigger as-child v-if="!props.hideCloseButton">
                                <UiButton variant="ghost" shape="square" hover-style="highlight" aria-label="Close dialog"
                                    :class="props.appearance === 'compact' ? 'absolute right-0 top-0 !h-10 !min-h-10 !w-10 !min-w-10 !rounded-none !rounded-bl-lg !rounded-tr-lg !bg-se-gray-300 !p-2' : ''">
                                    <template #before_label>
                                        <UiIcon name="close" class="w-6 h-6 text-se-dark" />
                                    </template>
                                </UiButton>
                            </Dialog.CloseTrigger>
                        </div>
                        <Dialog.Description v-if="props.text">
                            <UiText as="p" :variant="props.appearance === 'compact' ? 'p' : 'h6'" :class="props.appearance === 'compact' ? 'text-se-dark' : 'text-se-gray-700 mt-2'" :text="props.text" />
                        </Dialog.Description>
                    </div>

                    <!-- Body slot -->
                    <div class="overflow-y-auto" :class="{ 'se-no-scrollbar': props.hideScrollbar }">
                        <slot name="default" />
                    </div>

                    <!-- Actions -->
                    <div v-if="props.primaryActionLabel || props.secondaryActionLabel"
                        class="flex flex-col gap-3">
                        <UiButton v-if="props.primaryActionLabel" :label="props.primaryActionLabel" :variant="props.primaryActionVariant" :show-arrow="props.arrowsOnActionButtons"
                            class="flex-1" @click="emit('primaryAction')" :disabled="isLoading" />
                        <UiButton v-if="props.secondaryActionLabel" :label="props.secondaryActionLabel"
                            variant="outline" class="flex-1" @click="emit('secondaryAction')" :disabled="isLoading" :show-arrow="props.arrowsOnActionButtons" />
                        <slot name="actions" />
                    </div>
                    <slot name="footer" />

                </Dialog.Content>
            </Dialog.Positioner>
        </Dialog.Root>
    </Teleport>
</template>
